/**
 * Seedance 前端积分估算 —— 镜像后端 server/utils/kinovi.ts 的 seedanceCreditCost。
 * 后端为准；此处仅用于展示，两边必须保持同一套表。
 *
 * 口径（BytePlus 官方 token 计费 × 5 毛利 × 20 credits/USD）：
 *   tokens   = 像素面积 × 24fps × 计费秒数 / 1024
 *   计费秒数 = 输出时长 + (有参考视频 ? 参考视频总时长 : 0)
 * 图片数量与音频不影响价格；各宽高比等像素面积，无需宽高比因子；
 * 单任务积分向上取整到 5 的倍数（只向上，毛利只增不减）。
 */

export const REF_VIDEO_MAX_SECONDS = 15;

// 每档分辨率的像素面积（取生产实测输出尺寸，等面积覆盖所有宽高比）
const TIER_PIXELS: Record<string, number> = {
  '480': 428_544, // 864×496
  '720': 921_600, // 1280×720
  '1080': 2_073_600, // 1920×1080
  '4k': 8_294_400, // 3840×2160
};

const USD_PER_M_TOKENS = {
  pro: {
    noVideo: { low: 7, high: 7.7 },
    withVideo: { low: 4.3, high: 4.7 },
  },
  fast: {
    noVideo: { low: 5.6, high: 5.6 },
    withVideo: { low: 3.3, high: 3.3 },
  },
  // mini 官方价未公布 —— pro ×50% 暂估；仅 480/720p
  mini: {
    noVideo: { low: 3.5, high: 3.85 },
    withVideo: { low: 2.15, high: 2.35 },
  },
  // MiniMax H3 —— Seedance 2.0 Mini ×0.8（全线最低价）；
  // 两档都要 ×0.8，与后端 server/utils/minimax-h3.ts 保持同一套表
  h3: {
    noVideo: { low: 2.8, high: 3.08 }, // mini 3.5 / 3.85 × 0.8
    withVideo: { low: 1.72, high: 1.88 }, // mini 2.15 / 2.35 × 0.8
  },
} as const;

/** 模型名 → 计价家族（h3/fast/mini/pro） */
function priceFamily(model?: string): 'pro' | 'fast' | 'mini' | 'h3' {
  const m = String(model ?? '');
  if (m.includes('h3')) return 'h3';
  if (m.includes('fast')) return 'fast';
  if (m.includes('mini')) return 'mini';
  return 'pro';
}

/**
 * Seedance 2.5 按秒计价（非 token）—— 必须与后端 seedance25CreditCost 逐字对齐。
 * 官方口径：无参考视频 = 单价 × 输出时长；有参考视频 = 单价 × (输入+输出)。
 * 1080p 取促销（25% OFF，2026-09-17 到期）结束后的原价，避免到期后毛利被动下滑。
 */
const USD_PER_SEC_25: Record<string, { noVideo: number; withVideo: number }> = {
  '480': { noVideo: 0.1197, withVideo: 0.0737 },
  '720': { noVideo: 0.2717, withVideo: 0.1612 },
  '1080': { noVideo: 0.6604, withVideo: 0.3947 },
};
/** 2.5 毛利 3x（其余模型 5x） */
const MARGIN_25 = 3;
export const REF_VIDEO_MAX_SECONDS_25 = 30;
export const REF_VIDEO_MIN_SECONDS_25 = 1.8;

export function isSeedance25(model?: string): boolean {
  return model === 'seedance_2_5';
}

/**
 * Wan 3.0 按秒计价（非 token）—— 必须与后端 server/utils/dashscope.ts 的
 * wan30CreditCost 逐字对齐。
 * 阿里云百炼新加坡地域官方单价，换算 1 USD = 20 credits、毛利 8x（= usd × 160），
 * 与本仓库迁移前的 i2v_wan_3_0 价目表逐格一致。
 * 参考视频时长计入计费秒数（官方「输出 + 参考视频」合计 ≤30s 是同一计费口径）。
 */
const USD_PER_SEC_WAN30: Record<string, number> = {
  '480': 0.05,
  '720': 0.1,
  '1080': 0.2,
};
/** Wan 3.0 毛利 8x（其余模型 5x，2.5 是 3x） */
const MARGIN_WAN30 = 8;
export const REF_VIDEO_MAX_SECONDS_WAN30 = 15;
/** Wan 3.0 时长范围（整数秒）与「输出 + 参考视频」合计上限 */
export const WAN30_MIN_DURATION = 2;
export const WAN30_MAX_DURATION = 30;
export const WAN30_TOTAL_SECONDS_BUDGET = 30;

export function isWan30(model?: string): boolean {
  return model === 'wan_3_0';
}

function estimateWan30Credits(tier: string, duration: number, hasVideoRef: boolean, refVideoSeconds?: number): number {
  const t = USD_PER_SEC_WAN30[tier] ? tier : '720';
  const outSecs = Math.min(WAN30_MAX_DURATION, Math.max(WAN30_MIN_DURATION, Math.round(duration || 5)));
  const refSecs = hasVideoRef
    ? Math.min(
        REF_VIDEO_MAX_SECONDS_WAN30,
        Math.max(0.5, refVideoSeconds && refVideoSeconds > 0 ? refVideoSeconds : REF_VIDEO_MAX_SECONDS_WAN30),
      )
    : 0;
  const usd = USD_PER_SEC_WAN30[t] * (outSecs + refSecs);
  return Math.max(5, Math.ceil((usd * MARGIN_WAN30 * 20) / 5) * 5);
}

function estimate25Credits(tier: string, duration: number, hasVideoRef: boolean, refVideoSeconds?: number): number {
  const t = USD_PER_SEC_25[tier] ? tier : '720';
  const outSecs = Math.min(30, Math.max(4, duration || 5));
  if (!hasVideoRef) {
    const usd = USD_PER_SEC_25[t].noVideo * outSecs;
    return Math.max(5, Math.ceil((usd * MARGIN_25 * 20) / 5) * 5);
  }
  const srcSecs =
    refVideoSeconds && refVideoSeconds > 0
      ? Math.min(REF_VIDEO_MAX_SECONDS_25, Math.max(REF_VIDEO_MIN_SECONDS_25, refVideoSeconds))
      : REF_VIDEO_MAX_SECONDS_25;
  // 官方口径：输入时长 + 输出时长（与后端 seedance25CreditCost 逐字一致）
  const usd = USD_PER_SEC_25[t].withVideo * (srcSecs + outSecs);
  return Math.max(5, Math.ceil((usd * MARGIN_25 * 20) / 5) * 5);
}

export function estimateSeedanceCredits(input: {
  /** 提交给后端的模型名（含 "fast" 走 fast 价，预置） */
  model?: string;
  resolution: string;
  duration: number;
  vidCount: number;
  /** 参考视频总时长（秒，前端探测）；有视频但未知（探测中）时按 15s 上限估 */
  refVideoSeconds?: number;
  batchCount: number;
}): number {
  // Wan 3.0 按秒计价，不走 token 公式
  if (isWan30(input.model)) {
    const r = String(input.resolution ?? '720p').replace(/p$/i, '').toLowerCase();
    return (
      estimateWan30Credits(r === '4k' ? '1080' : r, input.duration, input.vidCount > 0, input.refVideoSeconds) *
      Math.max(1, input.batchCount)
    );
  }
  // 2.5 按秒计价，不走 token 公式
  if (isSeedance25(input.model)) {
    const r = String(input.resolution ?? '720p').replace(/p$/i, '').toLowerCase();
    return (
      estimate25Credits(r === '4k' ? '1080' : r, input.duration, input.vidCount > 0, input.refVideoSeconds) *
      Math.max(1, input.batchCount)
    );
  }
  const family = priceFamily(input.model);
  const res = String(input.resolution ?? '720p')
    .replace(/p$/i, '')
    .toLowerCase();
  let tier = res === '4k' ? '4k' : TIER_PIXELS[res] ? res : '720';
  // fast/mini 仅 480/720 —— 超档一律按 720 计价（与后端自洽）
  if ((family === 'fast' || family === 'mini') && (tier === '1080' || tier === '4k')) tier = '720';
  // H3 最高 1080p —— 4k 按 1080p 计价（与后端 minimaxH3CreditCost 自洽）
  if (family === 'h3' && tier === '4k') tier = '1080';
  const pixels = TIER_PIXELS[tier];
  // H3 与 Seedance 同一口径：参考视频时长计入计费秒数
  const hasRefVideo = input.vidCount > 0;
  const refSecs = hasRefVideo
    ? Math.min(REF_VIDEO_MAX_SECONDS, Math.max(0.5, input.refVideoSeconds && input.refVideoSeconds > 0 ? input.refVideoSeconds : REF_VIDEO_MAX_SECONDS))
    : 0;
  const billedSecs = Math.max(1, input.duration) + refSecs;
  const band = tier === '1080' || tier === '4k' ? 'high' : 'low';
  const rate = USD_PER_M_TOKENS[family][hasRefVideo ? 'withVideo' : 'noVideo'][band];
  const tokens = (pixels * 24 * billedSecs) / 1024;
  const usd = (tokens / 1_000_000) * rate;
  const perTask = Math.max(5, Math.ceil((usd * 5 * 20) / 5) * 5);
  return perTask * Math.max(1, input.batchCount);
}
